import {
  useInfiniteQuery,
  useQuery,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { feedApi } from '../api';
import type { FeedPost, CreatePostData, UpdatePostData, ReactionType } from '../types';

const KEYS = {
  all: ['feed'] as const,
  list: () => [...KEYS.all, 'list'] as const,
  detail: (id: string) => [...KEYS.all, 'detail', id] as const,
};

export function useFeed(limit = 20) {
  return useInfiniteQuery({
    queryKey: KEYS.list(),
    queryFn: async ({ pageParam }) => {
      const { data } = await feedApi.getFeed(pageParam, limit);
      return data;
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) =>
      lastPage.hasMore ? lastPage.nextCursor ?? undefined : undefined,
  });
}

export function usePostDetail(id: string | null) {
  return useQuery({
    queryKey: KEYS.detail(id!),
    queryFn: async () => {
      const { data } = await feedApi.getPost(id!);
      return data.post;
    },
    enabled: !!id,
  });
}

export function useCreatePost() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: CreatePostData) => {
      const { data: res } = await feedApi.createPost(data);
      return res.post;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEYS.list() });
    },
  });
}

export function useUpdatePost() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: UpdatePostData }) => {
      const { data: res } = await feedApi.updatePost(id, data);
      return res.post;
    },
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: KEYS.list() });
      qc.invalidateQueries({ queryKey: KEYS.detail(variables.id) });
    },
  });
}

export function useDeletePost() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await feedApi.deletePost(id);
      return id;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEYS.list() });
    },
  });
}

export function useTogglePin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await feedApi.togglePin(id);
      return { id, isPinned: data.isPinned };
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEYS.list() });
    },
  });
}

export function useToggleReaction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ postId, type }: { postId: string; type: ReactionType }) => {
      const { data } = await feedApi.toggleReaction(postId, type);
      return { postId, type, action: data.action };
    },
    onMutate: async ({ postId, type }) => {
      // Optimistic update
      await qc.cancelQueries({ queryKey: KEYS.list() });
      const previousData = qc.getQueryData(KEYS.list());

      qc.setQueryData(KEYS.list(), (old: any) => {
        if (!old) return old;
        return {
          ...old,
          pages: old.pages.map((page: any) => ({
            ...page,
            feed: page.feed.map((post: FeedPost) => {
              if (post.id !== postId) return post;
              const hasReaction = post.userReactions.includes(type);
              const prevReaction = post.userReactions.length > 0 ? post.userReactions[0] : null;
              const updatedCounts = { ...post.reactionCounts };

              if (hasReaction) {
                // Toggle off the same reaction
                updatedCounts[type] = Math.max(0, (updatedCounts[type] || 0) - 1);
                return { ...post, userReactions: [], reactionCounts: updatedCounts };
              }

              // Switching to a new reaction — decrement old, increment new
              if (prevReaction && prevReaction !== type) {
                updatedCounts[prevReaction] = Math.max(0, (updatedCounts[prevReaction] || 0) - 1);
              }
              updatedCounts[type] = (updatedCounts[type] || 0) + 1;
              return { ...post, userReactions: [type], reactionCounts: updatedCounts };
            }),
          })),
        };
      });

      return { previousData };
    },
    onError: (_err, _vars, context) => {
      if (context?.previousData) {
        qc.setQueryData(KEYS.list(), context.previousData);
      }
    },
  });
}

export function useRecordView() {
  return useMutation({
    mutationFn: (postId: string) => feedApi.recordView(postId),
  });
}
